/* eslint-disable @typescript-eslint/no-explicit-any */
import { Divider, Popover } from 'antd';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { BiFilterAlt } from 'react-icons/bi';
import { HiSortAscending, HiSortDescending } from 'react-icons/hi';
import { IoSearch } from 'react-icons/io5';
import { Input } from '../Input';
import { Pagination } from '../Pagination';
import { useTheme, withTheme } from '../ThemeProvider';
import {
    CountBadge,
    StyledHeaderBar,
    StyledTable,
    TableWrapper,
} from './style';
import { TableProps } from './types';
import { GoArrowUp, GoArrowDown } from 'react-icons/go';
import { BiSolidHide, BiSolidShow } from 'react-icons/bi';
import { HiOutlineBars4 } from 'react-icons/hi2';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Button } from '../Button';
import { IoCloseOutline } from 'react-icons/io5';
import { RiArrowUpDownFill } from 'react-icons/ri';
import { EButtonIntents } from '@src/typings';
import { Switch } from '../Switch';

function withDefaultSortIcon<T>(cols: ColumnsType<T>): ColumnsType<T> {
    return cols.map((col) => {
        if (!col.sorter) return col;

        return {
            ...col,
            sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) =>
                sortOrder === 'ascend' ? (
                    <HiSortAscending
                        size={18}
                        className={`${sortOrder === 'ascend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
                    />
                ) : sortOrder === 'descend' ? (
                    <HiSortDescending
                        size={18}
                        className={`${sortOrder === 'descend' ? 'text-primaryBlue' : 'text-primaryBlack'} mt-1`}
                    />
                ) : (
                    <RiArrowUpDownFill width={18} className="mt-1" />
                ),
        } as ColumnType<T>;
    });
}
const addNewHideVisibleColumn = (tableName, colKey, setHiddenInfo) => {
    const key = `hiddenColumns_${tableName}`;
    const prev = JSON.parse(localStorage.getItem(key) || '[]');
    let updated;
    if (prev.includes(colKey)) updated = prev.filter((c) => c !== colKey);
    else updated = [...prev, colKey];
    localStorage.setItem(key, JSON.stringify(updated));
    setHiddenInfo(updated);
};

function withFilter<T>(
    cols: ColumnsType<T>,
    setSortInfo: React.Dispatch<React.SetStateAction<any>>,
    sortInfo: any,
    setHiddenInfo: React.Dispatch<React.SetStateAction<any>>,
    tableName: string,
    setShowColumnManagement: React.Dispatch<React.SetStateAction<boolean>>,
    setSearchColumns: React.Dispatch<React.SetStateAction<string>>,
    handleFilterClick?: () => void,
    onChange?: (value: any) => void
) {
    return cols.map((col) => {
        if (!(col as any).columnFilter) return col;
        const { hasSort, hasFilter, hasManageColumn } = (col as any)
            .columnFilter;
        return {
            ...col,
            filterIcon: <BsThreeDotsVertical className="w-4 h-4 text-gray-9" />,
            filterDropdown: ({ close }) => (
                <div className="flex gap-0.5 flex-col rounded-2xl p-3 min-w-40">
                    {hasSort && (
                        <>
                            <div
                                className="flex gap-2 items-center font-semibold text-sm h-10  text-gray-4 px-3 rounded-lg hover:bg-black-10 cursor-pointer"
                                onClick={() => {
                                    const currentSortInfo =
                                        sortInfo?.columnKey === col.key
                                            ? sortInfo?.order
                                            : null;
                                    setSortInfo({
                                        columnKey: col.key,
                                        order:
                                            currentSortInfo === 'ascend'
                                                ? null
                                                : 'ascend',
                                    });
                                    close();
                                    onChange?.({
                                        _pagination: '',
                                        _filters: '',
                                        sorter: {
                                            columnKey:
                                                currentSortInfo === 'ascend'
                                                    ? ''
                                                    : col.key,
                                            order:
                                                currentSortInfo === 'descend' ||
                                                !currentSortInfo
                                                    ? 'ascend'
                                                    : '',
                                            field:
                                                currentSortInfo === 'ascend'
                                                    ? ''
                                                    : col.key,
                                            column: col,
                                        },
                                    });
                                }}
                            >
                                <GoArrowUp className="w-4 h-4 mt-1 text-green-700" />
                                <span>Sort by ASC</span>
                            </div>
                            <div
                                className="flex gap-2 items-center font-semibold text-sm h-10  text-gray-4 px-3 rounded-lg hover:bg-black-10 cursor-pointer"
                                onClick={() => {
                                    const currentSortInfo =
                                        sortInfo?.columnKey === col.key
                                            ? sortInfo?.order
                                            : null;
                                    setSortInfo({
                                        columnKey: col.key,
                                        order:
                                            currentSortInfo === 'descend'
                                                ? null
                                                : 'descend',
                                    });
                                    close();
                                    onChange?.({
                                        _pagination: '',
                                        _filters: '',
                                        sorter: {
                                            columnKey:
                                                currentSortInfo === 'descend'
                                                    ? ''
                                                    : col.key,
                                            order:
                                                currentSortInfo === 'ascend' ||
                                                !currentSortInfo
                                                    ? 'descend'
                                                    : '',
                                            field:
                                                currentSortInfo === 'descend'
                                                    ? ''
                                                    : col.key,
                                            column: col,
                                        },
                                    });
                                }}
                            >
                                <GoArrowDown className="w-4 h-4 mt-1 text-red-700" />
                                <span>Sort by DESC</span>
                            </div>
                            <Divider className="!my-0" />
                        </>
                    )}
                    {hasFilter && (
                        <>
                            <div
                                className="flex gap-2 items-center font-semibold text-sm h-10  text-gray-4 px-3 rounded-lg hover:bg-black-10 cursor-pointer"
                                onClick={() => {
                                    handleFilterClick?.();
                                    close();
                                }}
                            >
                                <BiFilterAlt className="w-4 h-4 mt-1" />
                                <span>Filter</span>
                            </div>
                            <Divider className="!my-0" />
                        </>
                    )}
                    {hasManageColumn && (
                        <>
                            <div
                                className="flex gap-2 items-center font-semibold text-sm h-10  text-gray-4 px-3 rounded-lg hover:bg-black-10  cursor-pointer"
                                onClick={() => {
                                    addNewHideVisibleColumn(
                                        tableName,
                                        col.key,
                                        setHiddenInfo
                                    );
                                    close();
                                }}
                            >
                                <BiSolidHide className="w-4 h-4" />
                                <span>Hide column</span>
                            </div>
                            <div
                                className="flex gap-2 items-center font-semibold text-sm h-10  text-gray-4 px-3 rounded-lg hover:bg-black-10 cursor-pointer"
                                onClick={() => {
                                    setSearchColumns('');
                                    setShowColumnManagement(true);
                                    close();
                                }}
                            >
                                <HiOutlineBars4 className="w-4 h-4 text-purple-500" />
                                <span>Manage columns</span>
                            </div>
                        </>
                    )}
                </div>
            ),
        } as ColumnType<T>;
    });
}

function TableComponent({
    title,
    count,
    dataSource = [],
    columns,
    onSearch,
    searchValue,
    searchPlaceholder = 'Search…',
    actions,
    selectable,
    selectedRowKeys: controlledKeys,
    onSelect,
    showPagination = true,
    paginationProps = {},
    onPageChange,
    loading,
    onChange,
    customEmpty,
    customEmptyText = 'No data',
    emptyState,
    footer,
    showSummary = true,
    summaryFormat = 'Showing {{start}} of {{total}} entries',
    rowKey,
    rowClassName,
    className,
    activeFilter = false,
    activeFilerContent,
    setFilterOpen,
    filterOpen = false,
    customHeader,
    headerActions,
    showCount = true,
    tableName = '',
    ...props
}: TableProps) {
    const { theme } = useTheme();
    const [sortInfo, setSortInfo] = useState<any>({});
    const [hiddenInfo, setHiddenInfo] = useState<any[]>([]);
    const [showColumnManagement, setShowColumnManagement] =
        useState<boolean>(false);
    const [searchColumns, setSearchColumns] = useState<string>('');
    // pagination state
    const pageSize = paginationProps?.pageSize ?? 10;
    const defaultPage = paginationProps?.defaultCurrent ?? 1;
    const [currentPage, setCurrentPage] = useState<number>(defaultPage);

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
        controlledKeys ?? []
    );
    const handleFilterClick = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setFilterOpen?.(!filterOpen);
    };

    let enhancedColumns = withDefaultSortIcon(columns || []);
    // enhancedColumns = withFilter(
    //     enhancedColumns,
    //     setSortInfo,
    //     sortInfo,
    //     setHiddenInfo,
    //     tableName,
    //     setShowColumnManagement,
    //     setSearchColumns,
    //     handleFilterClick,
    //     onChange
    //         ? ({ _pagination, _filters, sorter, extra }) =>
    //               onChange(_pagination, _filters, sorter, extra)
    //         : undefined
    // );

    useEffect(() => {
        if (paginationProps.current) {
            setCurrentPage(paginationProps.current);
        }
    }, [paginationProps.current]);

    // summary text
    const total = paginationProps.total ?? dataSource.length;
    const start = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const end = Math.min(currentPage * pageSize, total);

    const summaryText = summaryFormat
        .replace('{{start}}', start.toString())
        .replace('{{end}}', end.toString())
        .replace('{{total}}', total?.toString());

    // page change
    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        onPageChange?.(page, size);
    };

    // selection
    const getKey = (record: string): React.Key =>
        typeof rowKey === 'function'
            ? rowKey(record)
            : (record as any)[rowKey ?? 'key'];
    const handleSelectChange = (
        record: any,
        selected: boolean,
        _selectedRows: any[]
    ) => {
        const key = getKey(record);

        setSelectedRowKeys((prev) => {
            const newKeys = selected
                ? prev.includes(key)
                    ? prev
                    : [...prev, key]
                : prev.filter((k) => k !== key);

            onSelect?.(newKeys);

            return newKeys;
        });

        // if the parent passed an onSelect, send them the updated array
        onSelect?.(
            selected
                ? [...selectedRowKeys, key]
                : selectedRowKeys.filter((k) => k !== key)
        );
    };

    const handleSelectAll = (
        selected: boolean,
        allRows: any[],
        _changeRows: any[]
    ) => {
        const newKeys = selected ? allRows.map(getKey) : [];

        setSelectedRowKeys(newKeys);
        onSelect?.(newKeys);
    };
    const rowSelection = selectable
        ? {
              selectedRowKeys,
              onSelect: handleSelectChange,
              onSelectAll: handleSelectAll,
          }
        : undefined;

    useEffect(() => {
        if (controlledKeys) {
            setSelectedRowKeys(controlledKeys);
        }
    }, [controlledKeys]);

    const renderFilterIcon = () => {
        if (!activeFilter) return null;

        return (
            <Popover
                content={activeFilerContent}
                overlayInnerStyle={{ padding: 0 }}
                trigger="click"
                open={filterOpen}
                onOpenChange={(open) => {
                    setFilterOpen?.(open);
                }}
                placement="bottomLeft"
                destroyTooltipOnHide
                arrow={false}
            >
                <button
                    type="button"
                    className="mr-2 p-2 py-2.5 rounded-md cursor-pointer hover:bg-gray-50"
                    onClick={handleFilterClick}
                    aria-label="Open filter options"
                    aria-expanded={filterOpen}
                    style={{
                        border: `1px solid ${theme.colors.secondary.light.border}`,
                    }}
                >
                    <BiFilterAlt
                        size={16}
                        style={{
                            color: theme.colors.secondary.main.icon,
                        }}
                    />
                </button>
            </Popover>
        );
    };

    // If there's no data and emptyState is provided, show the empty state
    if (dataSource.length === 0 && emptyState) {
        return (
            <TableWrapper className={clsx(className)}>
                {(title || actions || onSearch) && (
                    <StyledHeaderBar>
                        <div className="flex items-center space-x-2">
                            {title && (
                                <h3
                                    className="font-semibold text-xl"
                                    style={{
                                        color: theme.colors.secondary.main.text,
                                    }}
                                >
                                    {title}
                                </h3>
                            )}
                            <CountBadge>0</CountBadge>
                        </div>
                        <div className="flex items-center space-x-2 justify-center">
                            <Input
                                prefix={renderFilterIcon()}
                                suffix={
                                    <IoSearch
                                        size={16}
                                        style={{
                                            color: theme.colors.secondary.main
                                                .icon,
                                        }}
                                    />
                                }
                                variant="outlined"
                                className={`!pr-3 min-h-12 ${activeFilter ? '!pl-1 !py-[5px]' : '!pl-3'}`}
                                placeholder={searchPlaceholder}
                                allowClear
                                onChange={onSearch}
                                value={searchValue}
                            />
                            {actions}
                        </div>
                    </StyledHeaderBar>
                )}
                {emptyState}
            </TableWrapper>
        );
    }
    const isColumnHidden = (col) => {
        return (
            localStorage
                .getItem(`hiddenColumns_${tableName}`)
                ?.includes(String(col.key || '')) ||
            hiddenInfo.includes(col.key) ||
            col.hidden
        );
    };
    const showAllColumns = () => {
        localStorage.removeItem(`hiddenColumns_${tableName}`);
        setHiddenInfo([]);
    };
    const hasHiddenColumn = useCallback(() => {
        try {
            const stored = JSON.parse(
                localStorage.getItem(`hiddenColumns_${tableName}`) || '[]'
            );
            return stored.length > 0 || hiddenInfo?.length > 0;
        } catch {
            return hiddenInfo?.length > 0;
        }
    }, [
        hiddenInfo,
        tableName,
        localStorage.getItem(`hiddenColumns_${tableName}`),
    ]);
    const ColumnsList = useMemo(() => {
        return enhancedColumns.map((col) => {
            if (
                !col.title ||
                (typeof col.title === 'string' &&
                    !col.title
                        .toLowerCase()
                        .includes(searchColumns.toLowerCase()))
            )
                return null;
            return (
                <div
                    key={col.key as string}
                    className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-gray-100"
                >
                    <span className="text-sm text-gray-700">
                        {typeof col.title === 'function'
                            ? col.title({})
                            : col.title}
                    </span>
                    <Switch
                        checked={!isColumnHidden(col)}
                        onChange={() => {
                            addNewHideVisibleColumn(
                                tableName,
                                col.key,
                                setHiddenInfo
                            );
                        }}
                    />
                </div>
            );
        });
    }, [enhancedColumns, tableName, setHiddenInfo, searchColumns]);

    return (
        <>
            <TableWrapper className={clsx(className)}>
                {(title || actions || onSearch) && !customHeader && (
                    <StyledHeaderBar>
                        <div className="flex items-center space-x-2">
                            {title && (
                                <h3
                                    className="font-semibold text-xl"
                                    style={{
                                        color: theme.colors.secondary.main.text,
                                    }}
                                >
                                    {title}
                                </h3>
                            )}
                            {!!showCount && (
                                <CountBadge>
                                    {count || dataSource.length}
                                </CountBadge>
                            )}
                        </div>
                        <div className="flex items-center space-x-2 justify-center">
                            <Input
                                prefix={renderFilterIcon()}
                                suffix={
                                    <IoSearch
                                        size={16}
                                        style={{
                                            color: theme.colors.secondary.main
                                                .icon,
                                        }}
                                    />
                                }
                                variant="outlined"
                                className={`!pr-3 min-h-12 ${activeFilter ? '!pl-1 !py-[5px]' : '!pl-3'}`}
                                placeholder={searchPlaceholder}
                                allowClear
                                onChange={onSearch}
                                value={searchValue}
                            />
                            {actions}
                        </div>
                    </StyledHeaderBar>
                )}
                {headerActions}

                {customHeader && customHeader}
                {/* {hasHiddenColumn() && (
                    <Button
                        icon={<BiSolidShow />}
                        onClick={showAllColumns}
                        className="mb-2 !bg-gray-9 !text-black border"
                    >
                        Show Hidden Columns
                    </Button>
                )} */}
                <StyledTable
                    {...props}
                    columns={enhancedColumns.map((c) => {
                        return {
                            ...c,
                            sortOrder:
                                sortInfo.columnKey === c.key
                                    ? sortInfo.order
                                    : null,
                            hidden: isColumnHidden(c),
                        };
                    })}
                    onChange={(pagination, filters, sorter, extra) => {
                        setSortInfo(sorter as any);
                        onChange?.(pagination, filters, sorter, extra);
                    }}
                    dataSource={dataSource}
                    loading={{
                        spinning:
                            typeof loading === 'boolean' ? loading : !!loading,
                        className: 'h-full !max-h-[unset]',
                        indicator: (
                            <AiOutlineLoading3Quarters
                                size={24}
                                className="animate-spin"
                                style={{
                                    color: theme.colors.primary.main.filled,
                                }}
                            />
                        ),
                    }}
                    pagination={false}
                    rowKey={rowKey}
                    rowClassName={rowClassName}
                    locale={{ emptyText: customEmpty ?? customEmptyText }}
                    rowSelection={rowSelection}
                />

                {(showSummary || showPagination) && (
                    <div
                        className="flex justify-between items-center mt-4 px-2 text-sm"
                        style={{ color: '#4b5563' }}
                    >
                        {showSummary && <div>{summaryText}</div>}
                        {showPagination && (
                            <Pagination
                                {...paginationProps}
                                total={total}
                                pageSize={pageSize}
                                defaultCurrent={defaultPage}
                                onChange={handlePageChange}
                            />
                        )}
                    </div>
                )}
                {footer}

                {showColumnManagement && (
                    <div className="absolute z-50 rounded-lg shadow-xl top-40 p-5 w-80 border-gray-5 bg-white border left-2">
                        <div className="flex items-center justify-between">
                            <Button
                                className="absolute top-1 right-1 !rounded-full flex items-center justify-center !p-1 !bg-gray-6 hover:!bg-gray-7 !text-gray-9 hover:!text-black"
                                onClick={() => setShowColumnManagement(false)}
                                intent={EButtonIntents.Text}
                            >
                                <IoCloseOutline size={22} />
                            </Button>
                        </div>
                        <div>
                            <div className="mb-4">
                                <span className="block text-sm font-medium text-gray-700 mb-2">
                                    Find column
                                </span>
                                <Input
                                    placeholder="Search column"
                                    onChange={(e) =>
                                        setSearchColumns(e.target.value)
                                    }
                                />
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {ColumnsList}
                            </div>
                            <Divider />
                            <div className="flex justify-between border-gray-200">
                                <Button
                                    intent={EButtonIntents.Text}
                                    className="!text-gray-9 hover:!text-blue-6"
                                    onClick={showAllColumns}
                                >
                                    Show all
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </TableWrapper>
        </>
    );
}

TableComponent.displayName = 'TableComponent';

export const Table = withTheme(TableComponent);
